import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  User,
  Settings,
  LogOut,
  Menu,
  Monitor,
  CalendarClock,
  Workflow,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Code
} from 'lucide-react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { cn } from '../lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
// ProviderSelector removed - using Anchor Browser exclusively

interface LayoutProps {
  children: ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Chat Workflow', href: '/chat-workflow', icon: MessageSquare },
  { name: 'Authoring', href: '/authoring', icon: Code },
  { name: 'Workflows', href: '/workflows', icon: Workflow },
  { name: 'Runs', href: '/runs', icon: Monitor },
  { name: 'Scheduled Tasks', href: '/scheduled-tasks', icon: CalendarClock },
  { name: 'AI Personas', href: '/personas', icon: User },
  { name: 'Settings', href: '/settings', icon: Settings },
]

const NavigationContent = ({
  location,
  user,
  clerk,
  isCollapsed = false,
  onToggleCollapse
}: {
  location: { pathname: string },
  user: any,
  clerk: any,
  isCollapsed?: boolean,
  onToggleCollapse?: () => void
}) => (
  <>
    <div className="flex h-16 items-center justify-between px-6 border-b">
      {!isCollapsed && (
        <div className="flex items-center space-x-2">
          <span className="text-xl">🏭</span>
          <h1 className="text-xl font-bold">Smokestack</h1>
        </div>
      )}
      {isCollapsed && (
        <span className="text-xl mx-auto">🏭</span>
      )}
      {onToggleCollapse && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className={cn("ml-auto", isCollapsed && "mx-auto")}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>

    <nav className="mt-6 px-3">
      <ul className="space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href
          return (
            <li key={item.name}>
              <Link
                to={item.href}
                className={cn(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  isCollapsed && 'justify-center'
                )}
                title={isCollapsed ? item.name : undefined}
              >
                <item.icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    !isCollapsed && 'mr-3',
                    isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'
                  )}
                />
                {!isCollapsed && item.name}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>

    {/* User info */}
    <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
      <div className={cn("flex items-center", isCollapsed && "justify-center")}>
        <Avatar className="h-8 w-8">
          <AvatarFallback>
            {user?.emailAddresses?.[0]?.emailAddress?.charAt(0).toUpperCase() ||
             user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        {!isCollapsed && (
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.emailAddresses?.[0]?.emailAddress}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.firstName} {user?.lastName}
            </p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => clerk.signOut()}
          title="Sign out"
          className={cn(!isCollapsed && "ml-auto")}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  </>
)

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { user } = useUser()
  const clerk = useClerk()
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Force collapsed navigation on workflow pages and authoring page for space optimization
  const isWorkflowPage = location.pathname.startsWith('/workflows')
  const isAuthoringPage = location.pathname.startsWith('/authoring')
  const isFullWidthPage = isWorkflowPage || isAuthoringPage
  const effectiveCollapsed = isFullWidthPage ? true : isCollapsed

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar - Always visible, collapsed on workflow pages */}
      <div className={cn(
        "hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:bg-card lg:shadow-lg transition-all duration-300",
        effectiveCollapsed ? "lg:w-16" : "lg:w-64"
      )}>
        <NavigationContent
          location={location}
          user={user}
          clerk={clerk}
          isCollapsed={effectiveCollapsed}
          onToggleCollapse={isFullWidthPage ? undefined : () => setIsCollapsed(!isCollapsed)}
        />
      </div>

      {/* Mobile Navigation - Hidden on full-width pages for full mobile experience */}
      {!isFullWidthPage && (
        <div className="lg:hidden">
          <div className="flex h-16 items-center justify-between px-4 border-b bg-card">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🏭</span>
              <h1 className="text-xl font-bold">Smokestack</h1>
            </div>
            <div className="flex items-center space-x-2">
              {/* Provider selector removed - using Anchor Browser exclusively */}
              <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <NavigationContent location={location} user={user} clerk={clerk} />
              </SheetContent>
            </Sheet>
            </div>
          </div>
        </div>
      )}

      {/* Main content - Optimized spacing */}
      <div className={cn(
        "transition-all duration-300",
        isFullWidthPage
          ? "lg:pl-16" // Collapsed nav width on full-width pages
          : (effectiveCollapsed ? "lg:pl-16" : "lg:pl-64") // Normal sidebar spacing on other pages
      )}>
        <main className={cn(
          isFullWidthPage ? "py-0" : "py-6" // Remove padding on full-width pages for full height
        )}>
          <div className={cn(
            isFullWidthPage
              ? "h-screen" // Full height on full-width pages
              : "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" // Normal container on other pages
          )}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
